
import { createContext, useContext, useEffect, useState } from "react";
import { User, AuthError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

interface AuthContextType {
  user: User | null;
  profile: any | null;
  isLoading: boolean;
  isAdmin: boolean;
  isAdminLoading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  isLoading: true,
  isAdmin: false,
  isAdminLoading: true,
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Separate query for admin role check - ensures it always runs on mount
  const { data: isAdmin = false, isLoading: isAdminLoading } = useQuery({
    queryKey: ["admin-role", user?.id],
    queryFn: async () => {
      if (!user?.id) return false;
      
      const { data: adminRole } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();

      return !!adminRole;
    },
    enabled: !!user?.id,
    refetchOnMount: true,
    staleTime: 0, // Always refetch on mount to ensure fresh admin status
  });

  const { data: profile } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", user.id)
          .maybeSingle();
        
        if (error) {
          console.error("Error fetching profile:", error);
          toast.error("Failed to load profile data. Please try refreshing the page.");
          return null;
        }

        return data;
      } catch (error) {
        console.error("Unexpected error:", error);
        toast.error("An unexpected error occurred. Please try again later.");
        return null;
      }
    },
    enabled: !!user?.id,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  useEffect(() => {
    const handleAuthError = async (error: AuthError) => {
      if (error.message.includes('refresh_token_not_found') || 
          error.message.includes('session_not_found')) {
        // Clear stale session with local scope to remove cookies
        await supabase.auth.signOut({ scope: 'local' });
        setUser(null);
        
        // Clear any stale localStorage data
        try {
          localStorage.removeItem('sb-jknbrmgwzizdtvtnufwb-auth-token');
        } catch (e) {
          console.error('Failed to clear localStorage:', e);
        }
        
        toast.error("Your session has expired. Please sign in again.");
      }
    };

    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        handleAuthError(error);
      } else {
        setUser(session?.user ?? null);
      }
      setIsLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Detect and handle auth errors in state changes
      if (event === 'TOKEN_REFRESHED') {
        setUser(session?.user ?? null);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
      } else if (event === 'USER_UPDATED' && !session) {
        // Session became invalid during update
        await handleAuthError(new Error('session_not_found') as AuthError);
      } else {
        setUser(session?.user ?? null);
        
        // Check for pending TPO invitation on sign in
        if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session?.user) {
          setTimeout(async () => {
            try {
              const { data } = await supabase.functions.invoke('accept-tpo-invitation');
              if (data?.accepted && !data?.already_admin) {
                console.log('TPO invitation accepted');
              }
            } catch (err) {
              console.error('Error checking TPO invitation:', err);
            }
          }, 0);
        }
      }
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ user, profile, isLoading, isAdmin, isAdminLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
